import { m4, m4FromTranslation, m4InverseRigid, m4Mul, type Mat4 } from '@/math/mat4';

export interface BoneDef {
  name: string;
  parent: number;
  bindPosX: number;
  bindPosY: number;
  bindPosZ: number;
}

export interface Skeleton {
  bones: ReadonlyArray<BoneDef>;
  bindLocals: Float32Array;
  bindInverses: Float32Array;
}

export interface Pose {
  locals: Float32Array;
  worlds: Float32Array;
  palette: Float32Array;
}

const SCRATCH_LOCAL = m4();
const SCRATCH_WORLD = m4();
const SCRATCH_INV = m4();

const writeMat = (out: Float32Array, boneIndex: number, src: Mat4): void => {
  const base = boneIndex * 16;
  for (let i = 0; i < 16; i++) out[base + i] = src[i]!;
};

const readMat = (out: Mat4, src: Float32Array, boneIndex: number): void => {
  const base = boneIndex * 16;
  for (let i = 0; i < 16; i++) out[i] = src[base + i]!;
};

export const makeSkeleton = (defs: ReadonlyArray<BoneDef>): Skeleton => {
  for (let i = 0; i < defs.length; i++) {
    const p = defs[i]!.parent;
    if (p >= i) throw new Error(`bone ${i} (${defs[i]!.name}) has parent ${p} >= self index`);
  }

  const bindLocals = new Float32Array(defs.length * 16);
  const bindInverses = new Float32Array(defs.length * 16);
  const tmpLocal = m4();
  const tmpWorld = m4();
  const tmpWorlds = new Float32Array(defs.length * 16);

  for (let i = 0; i < defs.length; i++) {
    const d = defs[i]!;
    m4FromTranslation(tmpLocal, d.bindPosX, d.bindPosY, d.bindPosZ);
    writeMat(bindLocals, i, tmpLocal);
    if (d.parent === -1) {
      writeMat(tmpWorlds, i, tmpLocal);
    } else {
      readMat(tmpWorld, tmpWorlds, d.parent);
      m4Mul(tmpWorld, tmpWorld, tmpLocal);
      writeMat(tmpWorlds, i, tmpWorld);
    }
    readMat(tmpWorld, tmpWorlds, i);
    m4InverseRigid(tmpWorld, tmpWorld);
    writeMat(bindInverses, i, tmpWorld);
  }

  return { bones: defs, bindLocals, bindInverses };
};

export const makePose = (skel: Skeleton): Pose => {
  const n = skel.bones.length;
  const pose: Pose = {
    locals: new Float32Array(n * 16),
    worlds: new Float32Array(n * 16),
    palette: new Float32Array(n * 16),
  };
  resetPose(skel, pose);
  return pose;
};

export const resetPose = (skel: Skeleton, pose: Pose): void => {
  pose.locals.set(skel.bindLocals);
};

// Write per-bone local matrices from Euler XYZ angles. eulers[i*3 + c] is the
// rotation around the c-th axis (0=X, 1=Y, 2=Z) for bone i. Translation stays
// at the bind position. Composition order: R = Rz * Ry * Rx.
export const poseFromEulers = (skel: Skeleton, pose: Pose, eulers: Float32Array): void => {
  const bones = skel.bones;
  const locals = pose.locals;
  for (let i = 0; i < bones.length; i++) {
    const b = bones[i]!;
    const rx = eulers[i * 3]!;
    const ry = eulers[i * 3 + 1]!;
    const rz = eulers[i * 3 + 2]!;
    const cx = Math.cos(rx);
    const sx = Math.sin(rx);
    const cy = Math.cos(ry);
    const sy = Math.sin(ry);
    const cz = Math.cos(rz);
    const sz = Math.sin(rz);
    const base = i * 16;
    locals[base + 0] = cy * cz;
    locals[base + 1] = cy * sz;
    locals[base + 2] = -sy;
    locals[base + 3] = 0;
    locals[base + 4] = sx * sy * cz - cx * sz;
    locals[base + 5] = sx * sy * sz + cx * cz;
    locals[base + 6] = sx * cy;
    locals[base + 7] = 0;
    locals[base + 8] = cx * sy * cz + sx * sz;
    locals[base + 9] = cx * sy * sz - sx * cz;
    locals[base + 10] = cx * cy;
    locals[base + 11] = 0;
    locals[base + 12] = b.bindPosX;
    locals[base + 13] = b.bindPosY;
    locals[base + 14] = b.bindPosZ;
    locals[base + 15] = 1;
  }
};

export const updatePose = (skel: Skeleton, pose: Pose): void => {
  const bones = skel.bones;
  for (let i = 0; i < bones.length; i++) {
    const b = bones[i]!;
    readMat(SCRATCH_LOCAL, pose.locals, i);
    if (b.parent === -1) {
      writeMat(pose.worlds, i, SCRATCH_LOCAL);
    } else {
      readMat(SCRATCH_WORLD, pose.worlds, b.parent);
      m4Mul(SCRATCH_WORLD, SCRATCH_WORLD, SCRATCH_LOCAL);
      writeMat(pose.worlds, i, SCRATCH_WORLD);
    }
    readMat(SCRATCH_WORLD, pose.worlds, i);
    readMat(SCRATCH_INV, skel.bindInverses, i);
    m4Mul(SCRATCH_WORLD, SCRATCH_WORLD, SCRATCH_INV);
    writeMat(pose.palette, i, SCRATCH_WORLD);
  }
};
